import { Component } from '@theme/component';
import { CONSTRUCTOR_EVENTS } from '@theme/constructor-utils';
import {
  isFlatViewActive,
  setFlatViewActive,
  setCardFlatImage,
  refreshCardFlatImage,
  FLAT_VIEW_CHANGED_EVENT,
} from '@theme/flat-view';

/** Dispatched by product-card-swatches.js after it re-renders a card's gallery. */
const GALLERY_UPDATED_EVENT = 'product-card:gallery-updated';

/**
 * Apply (or clear) the flat image on every Constructor card currently in the grid.
 * @param {boolean} active
 */
function applyFlatToAllCards(active) {
  document
    .querySelectorAll('constructor-results-list .card-gallery')
    .forEach((card) => setCardFlatImage(card, active));
}

/**
 * Reset every product-card carousel on the page to slide 1 so the swapped
 * flat/on-model image (always slide 1) is visible after a toggle.
 */
function resetAllCarousels() {
  document.querySelectorAll('constructor-results-list slideshow-component').forEach((el) => {
    const slideshow = /** @type {any} */ (el);
    if (typeof slideshow.select === 'function') {
      slideshow.select(0, undefined, { animate: false });
    } else if (slideshow.current !== undefined) {
      slideshow.current = 0;
    }
  });
}

/**
 * Document-level listeners that reapply flat view when the grid changes. Attached
 * once for the page — there can be two toggle instances (main bar + sticky bar),
 * and running these per-instance would double-apply and corrupt the stored
 * original image.
 */
let globalListenersAttached = false;
function ensureGlobalListeners() {
  if (globalListenersAttached) return;
  globalListenersAttached = true;

  // Pagination / filtering re-renders the results list.
  document.addEventListener(CONSTRUCTOR_EVENTS.productsRendered, () => {
    if (isFlatViewActive()) applyFlatToAllCards(true);
  });

  // A swatch change re-rendered a single card's gallery.
  document.addEventListener(GALLERY_UPDATED_EVENT, (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    const { productCard, newCard } = detail;
    const cardGallery = productCard?.querySelector?.('.card-gallery');
    if (!cardGallery || !newCard || !cardGallery.closest('constructor-results-list')) return;
    refreshCardFlatImage(cardGallery, newCard);
  });
}

/**
 * Swaps the first slide of every Constructor product card to the product's _FLAT image and back.
 *
 * `flat-image-template.liquid` emits the flat image server-side as an inert
 * `<template data-flat-image>`, so it arrives with the batch-card fetch and the toggle costs no
 * runtime request.
 *
 * The button-wrapper is cloned into the sticky filter bar, so two instances can exist. State
 * lives on <html> (see flat-view.js); both sync via the `flat-view:changed` event.
 */
class ConstructorFlatToggle extends Component {
  connectedCallback() {
    super.connectedCallback();
    ensureGlobalListeners();
    document.addEventListener(FLAT_VIEW_CHANGED_EVENT, this.#syncPressed);
    // Reflect the current state (e.g. a sticky clone created while flat is on, or
    // a session-persisted toggle restored on a fresh PLP).
    this.#syncPressed();
    // If flat view was restored from a previous PLP, apply it to any cards that
    // rendered before this toggle connected (the productsRendered listener only
    // covers subsequent renders).
    if (isFlatViewActive()) applyFlatToAllCards(true);
    this.#enableTransitionsAfterPaint();
  }

  /**
   * Enable the switch's slide/colour transitions only after the restored state
   * has been committed to the screen, so a session-persisted toggle appears in
   * its correct position immediately instead of animating into place on load.
   */
  #enableTransitionsAfterPaint() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.classList.add('constructor-flat-toggle--ready'));
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(FLAT_VIEW_CHANGED_EVENT, this.#syncPressed);
  }

  /** Toggles between on-model and flat images. Wired via `on:click="/toggle"`. */
  toggle() {
    const flat = !isFlatViewActive();
    setFlatViewActive(flat); // notifies every instance to sync its pressed state
    applyFlatToAllCards(flat);
    resetAllCarousels();
  }

  /**
   * Mirror the site-wide state onto this instance's switch. The switch represents
   * "On Model" mode, which is the default: pressed/active = on-model (flat off),
   * unpressed = flat. So pressed state is the inverse of flat view being active.
   */
  #syncPressed = () => {
    const onModel = !isFlatViewActive();
    this.querySelector('button')?.setAttribute('aria-pressed', String(onModel));
    this.classList.toggle('constructor-flat-toggle--active', onModel);
  };
}

if (!customElements.get('constructor-flat-toggle')) {
  customElements.define('constructor-flat-toggle', ConstructorFlatToggle);
}
